/**
 * Composite Asset Display Formatter
 *
 * Fixes composite asset HFN display format to use + separator for components
 * as per backend team specification.
 *
 * Backend stores: C.FUL.POP.002:1.001.002.001+2.001.002.001+3.003.003.001+4.003.001.001+5.001.002.001
 * Frontend was showing: C.FUL.POP.002:1.001.002.001:2.001.002.001:3.003.003.001:4.003.001.001:5.001.002.001
 *
 * This utility ensures correct display format.
 */

/**
 * Format composite asset HFN for display
 * @param hfn Raw HFN from backend
 * @param showComponents Whether to show component addresses (default: false for cards)
 * @returns Short format (C.FUL.ALL.004) or full format with components
 */
export const formatCompositeHfn = (hfn: string, showComponents: boolean = false): string => {
  if (!hfn || !hfn.includes(':')) {
    return hfn; // Not a composite asset
  }

  const parts = hfn.split(':');
  if (parts.length < 2) {
    return hfn; // Not a composite format
  }

  const baseHfn = parts[0]; // e.g., "C.FUL.ALL.004"

  // For asset cards and lists, show only the short format
  if (!showComponents) {
    return baseHfn;
  }

  // For detail views, show full format with components
  const components = parts.slice(1); // e.g., ["1.001.002.001", "2.001.002.001", ...]
  const formattedComponents = components.join('+');

  return `${baseHfn}:${formattedComponents}`;
};

/**
 * Format composite asset MFA for display
 * @param mfa Raw MFA from backend
 * @returns Properly formatted MFA with + separators for components
 */
export const formatCompositeMfa = (mfa: string): string => {
  if (!mfa || !mfa.includes(':')) {
    return mfa; // Not a composite asset
  }

  const parts = mfa.split(':');
  if (parts.length < 2) {
    return mfa; // Not a composite format
  }

  const baseMfa = parts[0]; // e.g., "9.003.001.002"
  const components = parts.slice(1); // e.g., ["1.001.002.001", "2.001.002.001", ...]

  // Join components with + separator
  const formattedComponents = components.join('+');

  return `${baseMfa}:${formattedComponents}`;
};

/**
 * Check if an asset is a composite asset
 * @param asset Asset object or HFN string
 * @returns Whether the asset is composite
 */
export const isCompositeAsset = (asset: any): boolean => {
  if (typeof asset === 'string') {
    return asset.startsWith('C.') && asset.includes(':');
  }

  return asset?.layer === 'C' && asset?.name?.includes(':');
};
